import {useParams} from 'react-router-dom'
import {Collapse, Space, CollapsePanelProps, Divider} from 'antd'
import useStore from 'base/store'
// import { PermissionType } from 'common/types'
import {RequestDetails, StatusHistory, useGetRequest} from 'modules/request'
import {User} from 'modules/users'
import {useOpen} from 'common/hooks'
import {
  Text,
  Loader,
  Button,
  Box,
  Modal,
  ButtonLink,
  UserAvatar,
  DropdownIcon,
  HistoryIcon,
  RequestIcon,
  ShieldChekSecondIcon,
  StarCircleIcon,
  ArrowToIcon,
  MessageIcon,
} from 'common/components'
import {formatDate} from 'common/helpers'
import {
  Conditions,
  ReviewForm,
  useGetProjectFeedback,
  useGetProject,
  ProjectFeedback,
} from '..'

interface ProjectMenuProps {
  requestUuid: string
}

interface ReviewPanelProps {
  projectUuid: string
  manager: User
  review: any
  projectClosed?: boolean
}

interface BorrowerFeedbackProps {
  feedback: ProjectFeedback
  notLast: boolean
}

const {Panel} = Collapse

const spaceSize = 16

const Icon = () => (
  <div>
    <DropdownIcon width={10} height="100%" />
  </div>
)

const ReviewPanel = ({
  projectUuid,
  manager,
  review,
  projectClosed,
  ...props
}: ReviewPanelProps) => {
  const [isOpen, handleOpen, handleClose] = useOpen(false)

  if (!projectClosed) return null

  return (
    <>
      <Panel
        {...(props as CollapsePanelProps)}
        header={
          <Space size={spaceSize}>
            <StarCircleIcon />
            <Text weight="demiBold">Manager review</Text>
          </Space>
        }
      >
        <Box ml={34} mr={34}>
          { !!review?.avgRating ? (
            <Space size={24}>
              <Space size={12}>
                <UserAvatar
                  size={44}
                  src={manager.photo}
                  alt={manager.firstName}
                />
                <div>
                  <Text size="2xl" weight="medium" mb={2}>
                    {review.avgRating}
                  </Text>
                  <Text size="xs">
                    <ButtonLink icon={<ArrowToIcon />} onClick={handleOpen}>
                      Average rating
                    </ButtonLink>
                  </Text>
                </div>
              </Space>
            </Space>
          ) : (
            <Button block disabled={!projectClosed} onClick={handleOpen}>
              Leave review
            </Button>
          )}
        </Box>
      </Panel>
      <Modal width={1078} open={isOpen} title="Review" onCancel={handleClose}>
        <ReviewForm
          projectUuid={projectUuid}
          review={review}
          onClose={handleClose}
        />
      </Modal>
    </>
  )
}

const BorrowerFeedback = ({feedback, notLast}: BorrowerFeedbackProps) => (
  <li>
    <Text size="xs" color="grey" mb={12}>
      {formatDate(feedback.createdAt)}
    </Text>
    <p>{feedback.text}</p>
    {notLast && <Divider />}
  </li>
)

export default function ProjectMenu({requestUuid}: ProjectMenuProps) {
  const permissions = useStore((state) => state.permissions)
  const params = useParams()
  const {data: request, isRefetching: isRequestRefetching} =
    useGetRequest(requestUuid)
  const {data: project} = useGetProject(params.uuid)
  const {data: projectFeedback} = useGetProjectFeedback(params.uuid)
  const role = useStore((store) => store.role)

  if (!request || !project || !projectFeedback) return <Loader />

  const isClosed = project.status === 'closed'

  return (
    <Collapse
      accordion
      bordered={false}
      expandIconPosition="end"
      expandIcon={Icon}
    >
      {role.manager && (
        <ReviewPanel
          key="4"
          projectClosed={isClosed}
          projectUuid={params.uuid}
          manager={project.manager}
          review={project.managerReview}
        />
      )}
      <Panel
        header={
          <Space size={spaceSize}>
            <HistoryIcon />
            <Text weight="demiBold">History</Text>
          </Space>
        }
        key="1"
      >
        <StatusHistory finished statusHistory={request.statusHistory} />
      </Panel>
      { !!permissions?.includes('add conditions') &&
      <Panel
        header={
          <Space size={spaceSize}>
            <ShieldChekSecondIcon />
            <Text weight="demiBold">Conditions</Text>
          </Space>
        }
        key="2"
      >
        <Conditions request={request} isRefetching={isRequestRefetching} />
      </Panel>
      }
      <Panel
        header={
          <Space size={spaceSize}>
            <RequestIcon />
            <Text weight="demiBold">Request details</Text>
          </Space>
        }
        key="3"
      >
        <RequestDetails projectView project={project} request={request} />
      </Panel>
      {!role.manager && project.managerReview && (
        <ReviewPanel
          key="4"
          projectClosed={isClosed}
          projectUuid={params.uuid}
          manager={project.manager}
          review={project.managerReview}
        />
      )}
      {projectFeedback.length && (role.coordinator || role.director) && (
        <Panel
          header={
            <Space size={spaceSize}>
              <MessageIcon />
              <Text weight="demiBold">Borrower feedback</Text>
            </Space>
          }
          key="5"
        >
          <ul>
            {projectFeedback.map((feedback, index) => (
              <BorrowerFeedback
                key={index}
                feedback={feedback}
                notLast={index !== projectFeedback.length - 1}
              />
            ))}
          </ul>
        </Panel>
      )}
    </Collapse>
  )
}
